/* File: GoogleSyncMenu.jsx
 * Path: ananunadm/src/components/main/GoogleSyncMenu.jsx
 * Description: Header "Sync" modal — shows Calendar/Contacts/Drive/Photos sync status,
 * provider settings, and controls to enable/disable or manually sync them. Rows are omitted
 * (not just disabled) for users lacking the matching permission.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
	ActionIcon,
	Tooltip,
	Group,
	Stack,
	Text,
	Switch,
	Badge,
	Button,
	Loader,
	Modal,
	Card
} from "@mantine/core";
import { IconRefreshDot, IconPhoto } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import axiosApi from "@/helpers/api";
import config from "@/config";
import { useUser } from "@/helpers/userContext";
import { hasPermission } from "@/helpers/permissionResolver";
import CalendarSettings from "../calendar/CalendarSettings";

const SERVICES = [
	{ key: "calendar", label: "Calendar", permModule: "calendarIntegration" },
	{ key: "contacts", label: "Contacts", permModule: "contactSync" },
	{ key: "drive", label: "Drive", permModule: "driveSync" },
	{ key: "photos", label: "Photos", permModule: "photoSync", picker: true }
];

function formatLastSynced(value) {
	if (!value) return "Never synced";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "Never synced";
	return `Synced ${date.toLocaleString()}`;
}

export default function GoogleSyncMenu({ isMobile, trigger = "icon" }) {
	const { user } = useUser();
	const route = config.api.calendarIntegrations || "calendar-integrations";

	const [opened, setOpened] = useState(false);
	const [status, setStatus] = useState(null);
	const [loading, setLoading] = useState(false);
	const [busyKey, setBusyKey] = useState("");

	const visibleServices = useMemo(
		() => SERVICES.filter((service) => hasPermission(user, `${service.permModule}.read`)),
		[user]
	);

	const loadStatus = useCallback(async () => {
		if (!visibleServices.length) return;
		setLoading(true);
		try {
			const response = await axiosApi.get(`${route}/google/status`);
			setStatus(response.data);
		} catch (error) {
			notifications.show({
				color: "red",
				message: error?.response?.data?.error || "Could not load Google sync status"
			});
		} finally {
			setLoading(false);
		}
	}, [route, visibleServices.length]);

	useEffect(() => {
		if (opened) loadStatus();
	}, [opened, loadStatus]);

	useEffect(() => {
		function onMessage(event) {
			if (event?.data?.source !== "apit-calendar-integration") return;
			loadStatus();
		}
		window.addEventListener("message", onMessage);
		return () => window.removeEventListener("message", onMessage);
	}, [loadStatus]);

	useEffect(() => {
		const openGoogleSync = () => setOpened(true);
		window.addEventListener("apit-open-google-sync", openGoogleSync);
		return () => window.removeEventListener("apit-open-google-sync", openGoogleSync);
	}, []);

	const handleIntegrationChanged = useCallback(() => {
		loadStatus();
		window.dispatchEvent(new CustomEvent("apit-google-sync-changed"));
	}, [loadStatus]);

	async function toggleService(serviceKey, nextEnabled) {
		setBusyKey(serviceKey);
		try {
			const response = await axiosApi.patch(`${route}/google/services`, {
				[`${serviceKey}Enabled`]: nextEnabled
			});
			setStatus(response.data);
			if (serviceKey === "calendar") {
				window.dispatchEvent(new CustomEvent("apit-google-sync-changed"));
			}
		} catch (error) {
			notifications.show({
				color: "red",
				message: error?.response?.data?.error || `Could not update ${serviceKey} sync`
			});
		} finally {
			setBusyKey("");
		}
	}

	async function syncNow(serviceKey) {
		setBusyKey(serviceKey);
		try {
			const response = await axiosApi.post(`${route}/google/sync`, { services: [serviceKey] });
			setStatus(response.data?.status || status);
			if (serviceKey === "calendar") {
				window.dispatchEvent(new CustomEvent("apit-google-sync-changed"));
			}
			if (response.data?.results?.[serviceKey]?.pickerRequired) {
				window.dispatchEvent(new CustomEvent("apit-open-photos-picker"));
			} else {
				notifications.show({ color: "green", message: `${serviceKey} sync complete` });
			}
		} catch (error) {
			notifications.show({
				color: "red",
				message: error?.response?.data?.error || `Could not sync ${serviceKey}`
			});
		} finally {
			setBusyKey("");
		}
	}

	if (!visibleServices.length) return null;

	const connected = Boolean(status?.connected);

	return (
		<>
			{trigger === "button" ? (
				<Button
					fullWidth
					justify="center"
					p={6}
					size="sm"
					variant="light"
					leftIcon={<IconRefreshDot size="1rem" stroke={1.7} />}
					aria-label="Google sync settings"
					onClick={() => setOpened(true)}
				>
					Google sync
				</Button>
			) : (
				<Tooltip label="Google sync">
					<ActionIcon
						variant="subtle"
						size={isMobile ? "md" : "lg"}
						aria-label="Google sync settings"
						onClick={() => setOpened(true)}
					>
						<IconRefreshDot size={isMobile ? 18 : 20} stroke={1.5} />
					</ActionIcon>
				</Tooltip>
			)}

			<Modal
				opened={opened}
				onClose={() => setOpened(false)}
				title="Google Workspace Integration"
				size="lg"
				centered
				styles={{
					title: { fontWeight: 600 },
					content: {
						maxHeight: "calc(100dvh - 2rem)",
						borderRadius: 14,
						overflow: "hidden",
						display: "flex",
						flexDirection: "column"
					},
					body: { overflowY: "auto", minHeight: 0 }
				}}
			>
				{opened && (
					<Stack spacing="lg">
						<CalendarSettings
							embedded
							status={status}
							loading={loading}
							onChanged={handleIntegrationChanged}
						/>

						<Card withBorder radius="md" p="md">
							<Stack spacing="md">
								<Group position="apart" align="flex-start">
									<Group spacing="sm">
										<IconRefreshDot size={22} />
										<div>
											<Text weight={700}>What to sync</Text>
											<Text size="sm" color="dimmed">
												Choose the Google services APIT should keep in sync.
											</Text>
										</div>
									</Group>
									<Group spacing="xs">
										{loading && <Loader size="xs" />}
										<Badge color={connected ? "green" : "gray"} variant="light">
											{connected ? "Connected" : "Disconnected"}
										</Badge>
									</Group>
								</Group>

								{!connected && (
									<Text size="sm" color="dimmed">
										Connect your Google account above to enable service sync.
									</Text>
								)}

								{visibleServices.map((service) => {
									const serviceStatus = status?.services?.[service.key];
									const enabled = Boolean(serviceStatus?.enabled);
									const busy = busyKey === service.key;
									return (
										<Group key={service.key} position="apart" noWrap py={4}>
											<Stack spacing={0}>
												<Group spacing={6}>
													<Text size="sm">{service.label}</Text>
													<Badge size="xs" color={enabled ? "green" : "gray"} variant="light">
														{enabled ? "on" : "off"}
													</Badge>
												</Group>
												<Text size="xs" color="dimmed">
													{formatLastSynced(serviceStatus?.lastSyncedAt)}
												</Text>
												{serviceStatus?.lastError && (
													<Text size="xs" color="red">{serviceStatus.lastError}</Text>
												)}
											</Stack>
											<Group spacing={6} noWrap>
												<Switch
													size="sm"
													checked={enabled}
													disabled={busy || !connected}
													onChange={(event) => toggleService(service.key, event.currentTarget.checked)}
												/>
												{enabled && (
													<Tooltip label={service.picker ? "Choose photos to sync" : "Sync now"}>
														<Button
															size="xs"
															variant="light"
															leftIcon={service.picker
																? <IconPhoto size={14} />
																: <IconRefreshDot size={14} />}
															loading={busy}
															disabled={!connected}
															onClick={() => syncNow(service.key)}
														>
															Sync now
														</Button>
													</Tooltip>
												)}
											</Group>
										</Group>
									);
								})}
							</Stack>
						</Card>
					</Stack>
				)}
			</Modal>
		</>
	);
}
